'use client';

/**
 * Consent Engine — Cookie deletion, consent state parsing, and GCM management.
 *
 * This module is client-only due to document.cookie access in cookie deletion.
 * For server-safe pure logic (parsing, GCM derivation), the exported functions
 * parseOptanonConsent, isCategoryOptedOut, deriveGcmState, and isNonUsGeolocation
 * don't use browser APIs but are co-located here for cohesion.
 *
 * Source: CookieCompliance_Audit_PersistentCookiespostOptOut_March2026
 * Reference: https://developer.onetrust.com/onetrust/docs/javascript-api
 */
import { gtag } from './consent-gtag.ts';

// eslint-disable-next-line @typescript-eslint/naming-convention -- intentional constant enum
export const ONETRUST_CATEGORIES = {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  STRICTLY_NECESSARY: 'C0001',
  // eslint-disable-next-line @typescript-eslint/naming-convention
  PERFORMANCE: 'C0002',
  // eslint-disable-next-line @typescript-eslint/naming-convention
  FUNCTIONAL: 'C0003',
  // eslint-disable-next-line @typescript-eslint/naming-convention
  TARGETING: 'C0004',
} as const;

export type OneTrustCategory =
  (typeof ONETRUST_CATEGORIES)[keyof typeof ONETRUST_CATEGORIES];

/**
 * Cookie deletion descriptor.
 */
interface CookieDeletionEntry {
  name: string;
  isPattern?: boolean;
  domains: string[];
  paths?: string[];
  sameSite?: ('strict' | 'lax' | 'none')[];
}

/** Performance cookies (C0002) — from March 2026 compliance audit */
const performanceCookies: CookieDeletionEntry[] = [
  {
    name: '_clck',
    domains: ['.compassion.com', '.www.compassion.com'],
    sameSite: ['strict', 'lax'],
  },
  {
    name: '_clsk',
    domains: ['.compassion.com', '.www.compassion.com'],
    sameSite: ['strict', 'lax'],
  },
  {
    name: '_ga',
    domains: [
      '.compassion.com',
      '.charitableautoresources.com',
      '.www.compassion.com',
      '.optimizely.com',
    ],
    sameSite: ['strict', 'lax'],
  },
  {
    name: '_ga_',
    isPattern: true,
    domains: [
      '.compassion.com',
      '.www.compassion.com',
      '.forms.compassion.com',
      '.checkout.compassion.com',
      '.explorer.compassion.com',
      '.blog.compassion.com',
      '.charitableautoresources.com',
      '.optimizely.com',
    ],
    sameSite: ['strict', 'lax'],
  },
  {
    name: 'QuantumMetricSessionID',
    domains: ['.compassion.com', '.www.compassion.com'],
    sameSite: ['none', 'strict', 'lax'],
  },
  {
    name: 'QuantumMetricUserID',
    domains: ['.compassion.com', '.www.compassion.com'],
    sameSite: ['none', 'strict', 'lax'],
  },
  {
    name: 'ci_client',
    domains: [
      '.compassion.com',
      '.forms.compassion.com',
      '.www.compassion.com',
      '.explorer.compassion.com',
      '.journey.compassion.com',
      '.blog.compassion.com',
    ],
    sameSite: ['strict', 'lax', 'none'],
  },
  {
    name: 'ci_user_engagement',
    domains: [
      '.compassion.com',
      '.forms.compassion.com',
      '.www.compassion.com',
      '.explorer.compassion.com',
      '.journey.compassion.com',
      '.blog.compassion.com',
    ],
    sameSite: ['strict', 'lax', 'none'],
  },
  {
    name: 'pageCountCookie',
    domains: [
      '.compassion.com',
      '.forms.compassion.com',
      '.www.compassion.com',
      '.explorer.compassion.com',
      '.journey.compassion.com',
      '.blog.compassion.com',
    ],
    sameSite: ['strict', 'lax', 'none'],
  },
  { name: 'rsc', domains: ['.compassion.com'], sameSite: ['strict', 'lax'] },
  {
    name: 'utag_main',
    domains: ['.compassion.com'],
    sameSite: ['strict', 'lax', 'none'],
  },
];

/** Targeting cookies (C0004) — from March 2026 compliance audit */
const targetingCookies: CookieDeletionEntry[] = [
  {
    name: '_fbp',
    domains: ['.compassion.com'],
    sameSite: ['lax', 'strict', 'none'],
  },
  {
    name: '_fbc',
    domains: ['.compassion.com'],
    sameSite: ['lax', 'strict', 'none'],
  },
  {
    name: '_gcl',
    domains: ['.compassion.com', '.charitableautoresources.com'],
    sameSite: ['strict', 'lax'],
  },
  {
    name: '_gcl_au',
    domains: [
      '.compassion.com',
      '.charitableautoresources.com',
      '.optimizely.com',
    ],
    sameSite: ['strict', 'lax'],
  },
  {
    name: '_pin_unauth',
    domains: [
      '.compassion.com',
      '.checkout.compassion.com',
      '.auth.compassion.com',
    ],
    sameSite: ['strict', 'lax', 'none'],
  },
  {
    name: '_uetsid',
    domains: ['.compassion.com', '.www.compassion.com'],
    sameSite: ['strict', 'lax'],
  },
  {
    name: '_uetvid',
    domains: ['.compassion.com', '.www.compassion.com'],
    sameSite: ['strict', 'lax'],
  },
  {
    name: 'cai',
    domains: ['.compassion.com'],
    sameSite: ['strict', 'lax', 'none'],
  },
  {
    name: 'cav',
    domains: ['.compassion.com'],
    sameSite: ['strict', 'lax', 'none'],
  },
  {
    name: 'ct',
    domains: ['.www.compassion.com', '.compassion.com'],
    sameSite: ['strict', 'lax', 'none'],
  },
  {
    name: 'IR_15682',
    domains: ['.compassion.com', '.www.compassion.com'],
    sameSite: ['strict', 'lax', 'none'],
  },
  {
    name: 'IR_gbd',
    domains: ['.compassion.com', '.www.compassion.com'],
    sameSite: ['strict', 'lax', 'none'],
  },
  {
    name: 'TEAL',
    domains: ['.compassion.com', '.www.compassion.com'],
    sameSite: ['strict', 'lax', 'none'],
  },
  {
    name: 'optimizelyEndUserId',
    domains: ['.compassion.com', '.www.compassion.com', '.optimizely.com'],
    sameSite: ['strict', 'lax', 'none'],
  },
  {
    name: '_twpid',
    domains: ['.compassion.com', '.www.compassion.com'],
    sameSite: ['strict', 'lax', 'none'],
  },
  {
    name: 'sa-user-id',
    domains: ['.compassion.com', '.www.compassion.com'],
    sameSite: ['strict', 'lax', 'none'],
  },
  {
    name: 'sa-user-id-v2',
    domains: ['.compassion.com', '.www.compassion.com'],
    sameSite: ['strict', 'lax', 'none'],
  },
  {
    name: 'sa-user-id-v3',
    domains: ['.compassion.com', '.www.compassion.com'],
    sameSite: ['strict', 'lax', 'none'],
  },
];

const categoryCookieMap: Partial<
  Record<OneTrustCategory, CookieDeletionEntry[]>
> = {
  [ONETRUST_CATEGORIES.PERFORMANCE]: performanceCookies,
  [ONETRUST_CATEGORIES.TARGETING]: targetingCookies,
};

const expireCookie = (
  name: string,
  domain: string,
  path: string,
  sameSite: string,
): void => {
  const secure = sameSite === 'none' ? 'secure;' : '';
  // eslint-disable-next-line unicorn/no-document-cookie -- only way to expire cookies client-side
  document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=${path};domain=${domain};samesite=${sameSite};${secure}`;
  // eslint-disable-next-line unicorn/no-document-cookie
  document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=${path};samesite=${sameSite};${secure}`;
};

const expireCookieAllVariants = (
  cookieName: string,
  domains: string[],
  paths: string[],
  sameSiteValues: string[],
): void => {
  for (const domain of domains) {
    for (const path of paths) {
      for (const sameSite of sameSiteValues) {
        expireCookie(cookieName, domain, path, sameSite);
      }
    }
  }
};

/** Delete all cookies for a given OneTrust category. */
export const deleteCookiesByCategory = (category: OneTrustCategory): void => {
  const entries = categoryCookieMap[category];
  if (!entries) {
    return;
  }

  const currentCookies = document.cookie
    .split(';')
    .map((c) => c.trim().split('=')[0]);

  for (const entry of entries) {
    const paths = entry.paths ?? ['/'];
    const sameSiteValues = entry.sameSite ?? ['strict', 'lax'];
    const matchingNames = entry.isPattern
      ? currentCookies.filter((name) => name.startsWith(entry.name))
      : currentCookies.filter((name) => name === entry.name);

    for (const cookieName of matchingNames) {
      expireCookieAllVariants(cookieName, entry.domains, paths, sameSiteValues);
    }
  }
};

/**
 * Parse the OptanonConsent cookie value into a key-value map.
 */
export const parseOptanonConsent = (
  cookieValue: string | undefined,
): Record<string, string> => {
  if (!cookieValue) {
    return {};
  }
  const decoded = decodeURIComponent(cookieValue);
  const pairs: Record<string, string> = {};
  for (const segment of decoded.split('&')) {
    const [key, ...rest] = segment.split('=');
    if (key) {
      pairs[key] = rest.join('=');
    }
  }
  return pairs;
};

/** Determine if a category is opted out based on the OptanonConsent cookie value. */
export const isCategoryOptedOut = (
  optanonConsent: string | undefined,
  category: OneTrustCategory,
): boolean => {
  if (!optanonConsent) {
    return false;
  }
  return decodeURIComponent(optanonConsent).includes(`${category}:0`);
};

/**
 * Google Consent Mode v2 consent state.
 * Property names are dictated by Google's API and must remain snake_case.
 */
/* eslint-disable @typescript-eslint/naming-convention */
export interface GcmConsentState {
  analytics_storage: 'granted' | 'denied';
  ad_storage: 'granted' | 'denied';
  ad_user_data: 'granted' | 'denied';
  ad_personalization: 'granted' | 'denied';
  functionality_storage: 'granted' | 'denied';
  personalization_storage: 'granted' | 'denied';
  security_storage: 'granted' | 'denied';
}
/* eslint-enable @typescript-eslint/naming-convention */

/** Derive Google Consent Mode state from OneTrust consent. */
export const deriveGcmState = (
  optanonConsent: string | undefined,
  hasInteracted: boolean,
  isNonUsGeo: boolean,
): GcmConsentState => {
  if (!hasInteracted && isNonUsGeo) {
    return {
      analytics_storage: 'denied',
      ad_storage: 'denied',
      ad_user_data: 'denied',
      ad_personalization: 'denied',
      functionality_storage: 'granted',
      personalization_storage: 'granted',
      security_storage: 'granted',
    };
  }

  if (!hasInteracted) {
    return {
      analytics_storage: 'granted',
      ad_storage: 'granted',
      ad_user_data: 'granted',
      ad_personalization: 'granted',
      functionality_storage: 'granted',
      personalization_storage: 'granted',
      security_storage: 'granted',
    };
  }

  const performanceOptedOut = isCategoryOptedOut(
    optanonConsent,
    ONETRUST_CATEGORIES.PERFORMANCE,
  );
  const targetingOptedOut = isCategoryOptedOut(
    optanonConsent,
    ONETRUST_CATEGORIES.TARGETING,
  );

  return {
    analytics_storage: performanceOptedOut ? 'denied' : 'granted',
    ad_storage: targetingOptedOut ? 'denied' : 'granted',
    ad_user_data: targetingOptedOut ? 'denied' : 'granted',
    ad_personalization: targetingOptedOut ? 'denied' : 'granted',
    functionality_storage: 'granted',
    personalization_storage: 'granted',
    security_storage: 'granted',
  };
};

/** Detect if the current visitor is in a non-US geolocation. */
export const isNonUsGeolocation = (
  optanonConsent: string | undefined,
  searchParams?: string,
): boolean => {
  if (searchParams) {
    const params = new URLSearchParams(searchParams);
    const otgeo = params.get('otgeo');
    if (otgeo && otgeo.toLowerCase() !== 'us') {
      return true;
    }
  }

  const parsed = parseOptanonConsent(optanonConsent);
  const geo = parsed['geolocation'];
  if (geo) {
    const country = geo.split(';')[0]?.toUpperCase();
    if (country && country !== 'US') {
      return true;
    }
  }

  return false;
};

/**
 * Run the full consent enforcement flow:
 * 1. Delete cookies for opted-out categories
 * 2. Update Google Consent Mode (both granted and denied states)
 *
 * Single source of truth for consent enforcement — used by both
 * one-trust.tsx (on consent change) and consent-banner.tsx (on page load).
 */
export const enforceConsent = (optanonConsent: string | undefined): void => {
  if (!optanonConsent) {
    return;
  }

  if (isCategoryOptedOut(optanonConsent, ONETRUST_CATEGORIES.PERFORMANCE)) {
    deleteCookiesByCategory(ONETRUST_CATEGORIES.PERFORMANCE);
    gtag('consent', 'update', { analytics_storage: 'denied' });
  } else {
    gtag('consent', 'update', { analytics_storage: 'granted' });
  }

  if (isCategoryOptedOut(optanonConsent, ONETRUST_CATEGORIES.TARGETING)) {
    deleteCookiesByCategory(ONETRUST_CATEGORIES.TARGETING);
    gtag('consent', 'update', {
      ad_storage: 'denied',
      ad_user_data: 'denied',
      ad_personalization: 'denied',
    });
  } else {
    gtag('consent', 'update', {
      ad_storage: 'granted',
      ad_user_data: 'granted',
      ad_personalization: 'granted',
    });
  }
};
